import type {
  DepositAvailableBalance,
  DepositUnavailableBalance,
  DepositPayoutSchedule,
  DepositBalanceCurrency,
  DepositUnavailableBalanceCurrency,
  DepositRow,
} from '@/lib/services/deposits/deposits.types'
import type { StyledIconProps } from '@/components/ui/styled-icon'
import type { LucideIcon } from 'lucide-react'
import { detailDataListClass, detailInsetListClass } from '@/components/patterns/detail-page'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { StyledIcon } from '@/components/ui/styled-icon'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { Clock3, Landmark, Send } from 'lucide-react'
import {
  formatDepositDateTime,
  formatMinorCurrency,
  getDepositCurrencySuffix,
} from './-presentation'

type PipelineSnapshot =
  | Pick<
      DepositAvailableBalance,
      'available' | 'as_of' | 'currencies' | 'payout_schedule' | 'has_paid_deposit'
    >
  | (Pick<DepositUnavailableBalance, 'available' | 'as_of' | 'currencies'> & {
      payout_schedule?: never
      has_paid_deposit?: never
    })
type PipelineCurrency = DepositBalanceCurrency | DepositUnavailableBalanceCurrency
interface PipelineRow {
  amountMinor: number | null
  detail: string
  icon: LucideIcon
  label: string
  tone: StyledIconProps['tone']
}

const WEEKDAYS: Partial<Record<string, string>> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
}

// Florists choose their payout schedule, so describe the one they actually have.
export function getPayoutScheduleText(schedule?: DepositPayoutSchedule | null): string | null {
  if (schedule?.interval === 'daily') {
    return `Deposits are sent every business day. Payments usually reach your bank ${schedule.delay_days ?? 2} business days after they're made.`
  }
  if (schedule?.interval === 'weekly' && WEEKDAYS[String(schedule.weekly_anchor)]) {
    return `Deposits are sent every ${WEEKDAYS[String(schedule.weekly_anchor)]}.`
  }
  if (schedule?.interval === 'monthly' && schedule.monthly_anchor) {
    return `Deposits are sent once a month, on day ${schedule.monthly_anchor}.`
  }
  if (schedule?.interval === 'manual') return 'Deposits are sent only when you request them.'

  return null
}

function getReadyDetail(schedule: DepositPayoutSchedule | null | undefined): string {
  if (schedule?.interval === 'daily')
    return 'Goes out with your next deposit, usually the next business day.'
  if (schedule?.interval === 'weekly' && WEEKDAYS[String(schedule.weekly_anchor)]) {
    return `Goes out with your next deposit on ${WEEKDAYS[String(schedule.weekly_anchor)]}.`
  }

  return 'Goes out with your next scheduled deposit.'
}

function getPipelineRows(
  currency: PipelineCurrency,
  schedule: DepositPayoutSchedule | null | undefined
): PipelineRow[] {
  const rows = [
    {
      amountMinor: currency.on_the_way_minor,
      icon: Send,
      positiveLabel: 'On the way',
      positiveDetail: 'Deposits moving to your bank.',
      negativeLabel: 'Bank withdrawal on the way',
      negativeDetail: 'Money moving from your bank to cover an account balance.',
    },
    {
      amountMinor: currency.ready_for_payout_minor,
      icon: Landmark,
      positiveLabel: 'Ready for the next deposit',
      positiveDetail: getReadyDetail(schedule),
      negativeLabel: 'Balance to cover',
      negativeDetail: 'Future payments will cover this before more money can be deposited.',
    },
    {
      amountMinor: currency.still_processing_minor,
      icon: Clock3,
      positiveLabel: 'Still processing',
      positiveDetail:
        'Recent payments that are not available yet. They arrive over your next few deposits, not all at once.',
      negativeLabel: 'Pending deductions',
      negativeDetail: 'Refunds or other activity that will reduce a future deposit.',
    },
  ]

  return rows
    .filter(({ amountMinor }) => amountMinor != null && amountMinor !== 0)
    .map((row) => {
      const isNegative = Number(row.amountMinor) < 0

      return {
        amountMinor: row.amountMinor,
        detail: isNegative ? row.negativeDetail : row.positiveDetail,
        icon: row.icon,
        label: isNegative ? row.negativeLabel : row.positiveLabel,
        tone: isNegative ? 'warning' : 'neutral',
      }
    })
}

function PipelineBalanceRow({
  row,
  currency,
}: {
  row: PipelineRow
  currency: DepositRow['currency']
}) {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 px-3 py-3 @sm/pipeline:grid-cols-[auto_minmax(0,1fr)_auto] @sm/pipeline:items-center">
      <StyledIcon icon={row.icon} tone={row.tone} className="@sm/pipeline:self-center" />
      <div className="min-w-0 flex-1">
        <Typography variant="labelStrong" className="block">
          {row.label}
        </Typography>
        <Typography variant="supporting" className="mt-0.5 block">
          {row.detail}
        </Typography>
      </div>
      <Typography
        variant="labelStrong"
        tone={row.tone === 'warning' ? 'warning' : 'default'}
        className="col-start-2 mt-1 shrink-0 tabular-nums @sm/pipeline:col-start-3 @sm/pipeline:row-start-1 @sm/pipeline:mt-0"
      >
        {formatMinorCurrency(row.amountMinor, currency)}
      </Typography>
    </div>
  )
}

export function DepositPipelineCard({ snapshot }: { snapshot?: PipelineSnapshot | null }) {
  const currencies = snapshot?.currencies ?? []
  const scheduleText = getPayoutScheduleText(snapshot?.payout_schedule)
  const currencySections = currencies
    .map((currency) => ({ currency, rows: getPipelineRows(currency, snapshot?.payout_schedule) }))
    .filter(({ rows }) => rows.length)
  // Stripe holds a new account's first deposit while it verifies the account.
  const awaitingFirstDeposit =
    snapshot?.available &&
    snapshot.has_paid_deposit === false &&
    currencies.some(
      (currency) =>
        Number(currency.ready_for_payout_minor ?? 0) +
          Number(currency.still_processing_minor ?? 0) >
        0
    )

  if (!currencySections.length) return null

  return (
    <Card>
      <CardHeader className="gap-1.5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle>Money on its way</CardTitle>
            <CardDescription>
              Money from recent payments that has not reached your bank yet.
              {scheduleText ? ` ${scheduleText}` : ''}
            </CardDescription>
          </div>
          <Typography variant="caption">
            As of {formatDepositDateTime(snapshot?.as_of, 'just now')}
          </Typography>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {snapshot && !snapshot.available ? (
          <InfoNotice title="Current balance is temporarily unavailable" tone="warning" compact>
            We could not refresh the ready and processing amounts. Your deposit history is still
            available below.
          </InfoNotice>
        ) : null}

        {awaitingFirstDeposit ? (
          <InfoNotice title="Your first deposit" tone="neutral" compact>
            The first deposit for a new account usually arrives 7 to 14 days after your first
            payment while the account is verified. After that, deposits follow your regular
            schedule.
          </InfoNotice>
        ) : null}

        {currencySections.map(({ currency, rows }) => (
          <section
            key={currency.currency}
            className="space-y-3"
            aria-label={`Money on its way${getDepositCurrencySuffix(currency.currency)}`}
          >
            <div className={cn('@container/pipeline', detailInsetListClass, detailDataListClass)}>
              {rows.map((row) => (
                <PipelineBalanceRow key={row.label} row={row} currency={currency.currency} />
              ))}
            </div>
          </section>
        ))}
      </CardContent>
    </Card>
  )
}
